/**
 * Document Requirements by Loan Program
 * Scalable structure supporting 8 funding programs
 */

export const PROGRAMS = {
  'SBA Loan': {
    id: 'sba_loan',
    label: 'SBA Loan',
    icon: '🏛️',
    color: 'primary',
    description: 'U.S. SBA Loan Programs',
    documents: [
      { 
        type: 'pfs_pdf', 
        label: 'Personal Financial Statement (PFS)', 
        required: 1, 
        icon: '📋',
        description: 'Completed Personal Financial Statement form'
      },
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 3, 
        icon: '🏦',
        description: '3 most recent business checking account statements'
      },
      { 
        type: 'personal_bank_statement', 
        label: 'Personal Bank Statements', 
        required: 3, 
        icon: '💳',
        description: '3 most recent personal checking account statements'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Returns', 
        required: 2, 
        icon: '📊',
        description: '2 most recent business tax returns'
      },
      { 
        type: 'personal_tax_return', 
        label: 'Personal Tax Returns', 
        required: 2, 
        icon: '📝',
        description: '2 most recent personal tax returns'
      },
      { 
        type: 'profit_loss', 
        label: 'Profit & Loss Statement', 
        required: 1, 
        icon: '📈',
        description: 'Most recent P&L statement'
      },
      { 
        type: 'balance_sheet', 
        label: 'Balance Sheet', 
        required: 1, 
        icon: '📋',
        description: 'Most recent balance sheet'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'articles_incorporation', 
        label: 'Articles of Incorporation/Organization', 
        required: 1, 
        icon: '📄',
        description: 'Articles of Incorporation or Articles of Organization'
      },
      { 
        type: 'operating_agreement', 
        label: 'Operating Agreement', 
        required: 1, 
        icon: '📄',
        description: 'Business operating agreement'
      },
    ]
  },
  'Term Loan': {
    id: 'term_loan',
    label: 'Term Loan',
    icon: '📋',
    color: 'info',
    description: 'Multi-year Business Financing',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 3, 
        icon: '🏦',
        description: '3 most recent business checking account statements'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Returns', 
        required: 2, 
        icon: '📊',
        description: '2 most recent business tax returns'
      },
      { 
        type: 'profit_loss', 
        label: 'Profit & Loss Statement', 
        required: 1, 
        icon: '📈',
        description: 'Most recent P&L statement'
      },
      { 
        type: 'balance_sheet', 
        label: 'Balance Sheet', 
        required: 1, 
        icon: '📋',
        description: 'Most recent balance sheet'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
    ]
  },
  'Equipment Financing': {
    id: 'equipment_financing',
    label: 'Equipment Financing',
    icon: '⚙️',
    color: 'warning',
    description: 'Equipment Purchase Financing',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 3, 
        icon: '🏦',
        description: '3 most recent business checking account statements'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Returns', 
        required: 2, 
        icon: '📊',
        description: '2 most recent business tax returns'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
      { 
        type: 'equipment_quote', 
        label: 'Equipment Quote/Invoice', 
        required: 1, 
        icon: '📄',
        description: 'Quote or invoice for equipment being financed'
      },
    ]
  },
  'Working Capital Loan': {
    id: 'working_capital_loan',
    label: 'Working Capital Loan',
    icon: '💼',
    color: 'success',
    description: 'Short-term Business Operations Financing',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 3, 
        icon: '🏦',
        description: '3 most recent business checking account statements'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Returns', 
        required: 2, 
        icon: '📊',
        description: '2 most recent business tax returns'
      },
      { 
        type: 'profit_loss', 
        label: 'Profit & Loss Statement', 
        required: 1, 
        icon: '📈',
        description: 'Most recent P&L statement'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
    ]
  },
  'Line of Credit': {
    id: 'line_of_credit',
    label: 'Line of Credit',
    icon: '💳',
    color: 'secondary',
    description: 'Flexible Credit Line for Business',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 3, 
        icon: '🏦',
        description: '3 most recent business checking account statements'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Returns', 
        required: 1, 
        icon: '📊',
        description: '1 most recent business tax return'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
    ]
  },
  'Invoice Financing': {
    id: 'invoice_financing',
    label: 'Invoice Financing',
    icon: '📊',
    color: 'info',
    description: 'Cash Flow Financing Against Invoices',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 2, 
        icon: '🏦',
        description: '2 most recent business checking account statements'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Return', 
        required: 1, 
        icon: '📊',
        description: 'Most recent business tax return'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'sample_invoice', 
        label: 'Sample Invoice', 
        required: 1, 
        icon: '📄',
        description: 'Sample invoice you offer to customers'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
    ]
  },
  'Payroll Financing': {
    id: 'payroll_financing',
    label: 'Payroll Financing',
    icon: '👥',
    color: 'error',
    description: 'Payroll Funding Solution',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 2, 
        icon: '🏦',
        description: '2 most recent business checking account statements'
      },
      { 
        type: 'payroll_records', 
        label: 'Payroll Records', 
        required: 1, 
        icon: '📋',
        description: 'Most recent payroll processing records'
      },
      { 
        type: 'business_tax_return', 
        label: 'Business Tax Return', 
        required: 1, 
        icon: '📊',
        description: 'Most recent business tax return'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
    ]
  },
  'Business Cash Advance': {
    id: 'business_cash_advance',
    label: 'Business Cash Advance',
    icon: '💰',
    color: 'success',
    description: 'Quick Business Funding',
    documents: [
      { 
        type: 'business_bank_statement', 
        label: 'Business Bank Statements', 
        required: 3, 
        icon: '🏦',
        description: '3 most recent business checking account statements'
      },
      { 
        type: 'drivers_license', 
        label: 'Driver\'s License', 
        required: 1, 
        icon: '🪪',
        description: 'Copy of your driver\'s license'
      },
      { 
        type: 'voided_check', 
        label: 'Voided Check', 
        required: 1, 
        icon: '✓',
        description: 'Copy of a voided check'
      },
    ]
  }
};

/**
 * Program order - controls tab/button order in UI
 */
export const PROGRAM_ORDER = [
  'SBA Loan',
  'Term Loan',
  'Equipment Financing',
  'Working Capital Loan',
  'Line of Credit',
  'Invoice Financing',
  'Payroll Financing',
  'Business Cash Advance'
];

/**
 * Legacy exports for backward compatibility
 */
export const SBA_REQUIREMENTS = PROGRAMS['SBA Loan'];
export const ALL_OTHER_REQUIREMENTS = PROGRAMS['Business Cash Advance'];

export const DOCUMENT_CATEGORIES = {
  pfs_pdf: 'PFS',
  business_bank_statement: 'Financial',
  personal_bank_statement: 'Financial',
  business_tax_return: 'Tax',
  personal_tax_return: 'Tax',
  profit_loss: 'Financial',
  balance_sheet: 'Financial',
  drivers_license: 'Personal',
  voided_check: 'Financial',
  articles_incorporation: 'Legal',
  operating_agreement: 'Legal',
  equipment_quote: 'Financial',
  sample_invoice: 'Financial',
  payroll_records: 'Financial',
};

/**
 * Get requirements based on program name
 * @param {string} programName - Program name (e.g., 'SBA Loan', 'Term Loan')
 * @returns {object} Document requirements for the program
 */
export const getRequirementsByProgram = (programName) => {
  return PROGRAMS[programName] || PROGRAMS['Business Cash Advance'];
};

/**
 * Get program config by name
 * @param {string} programName - Program name
 * @returns {object} Program configuration
 */
export const getProgramConfig = (programName) => {
  const config = PROGRAMS[programName];
  if (!config) {
    console.warn(`Program "${programName}" not found. Available: ${PROGRAM_ORDER.join(', ')}`);
    return PROGRAMS[PROGRAM_ORDER[0]] || {};
  }
  return config;
};

/**
 * Get all available programs
 * @returns {array} Array of program names in order
 */
export const getAllPrograms = () => {
  return PROGRAM_ORDER;
};

/**
 * Calculate completion stats for a program
 * @param {array} documents - User's uploaded documents
 * @param {object} requirements - Program requirements
 * @returns {object} Completion statistics
 */
export const calculateCompletionStats = (documents, requirements) => {
  let totalDocumentsUploaded = 0;
  let totalDocumentsRequired = 0;
  const documentStats = {};

  requirements.documents.forEach(req => {
    totalDocumentsRequired += req.required;
    const uploaded = documents.filter(doc => doc.documentType === req.type).length;
    totalDocumentsUploaded += Math.min(uploaded, req.required);
    
    documentStats[req.type] = {
      ...req,
      uploaded: Math.min(uploaded, req.required),
      isComplete: uploaded >= req.required,
      remaining: Math.max(0, req.required - uploaded)
    };
  });

  const completionPercentage = totalDocumentsRequired > 0 
    ? Math.round((totalDocumentsUploaded / totalDocumentsRequired) * 100) 
    : 0;

  return {
    totalDocumentsUploaded,
    totalDocumentsRequired,
    completionPercentage,
    isComplete: totalDocumentsUploaded >= totalDocumentsRequired,
    documentStats,
    missingDocuments: Object.values(documentStats).filter(stat => !stat.isComplete)
  };
};

/**
 * Calculate completion stats across ALL programs for a user
 * @param {array} documents - User's uploaded documents
 * @returns {object} Map of program name -> completion stats
 */
export const calculateAllProgramStats = (documents) => {
  const allStats = {};
  
  PROGRAM_ORDER.forEach(programName => {
    const requirements = PROGRAMS[programName];
    allStats[programName] = calculateCompletionStats(documents, requirements);
  });
  
  return allStats;
};

/**
 * SBA Loan Package checklist items ordered for maximum completion.
 * "form" items link to external apps; "upload" items trigger file upload.
 * Order: quick-win uploads first, then forms, then harder uploads.
 */
export const SBA_CHECKLIST = [
  // --- FORMS TO COMPLETE ---
  {
    id: 'core_funding_application',
    kind: 'form',
    label: 'Core Funding Application',
    description: 'Complete your business funding application',
    icon: '📝',
    appType: 'application',
  },
  {
    id: 'pfs_sba_form_413',
    kind: 'form',
    label: 'PFS Copilot – SBA Form 413',
    description: 'Personal Financial Statement required for SBA loans',
    icon: '🏛️',
    appType: 'pfs',
  },
  {
    id: 'debt_schedule_builder',
    kind: 'form',
    label: 'Debt Schedule Builder',
    description: 'Itemize your business debts',
    icon: '📊',
    internalPath: '/debt-schedule',
  },
  // --- QUICK-WIN UPLOADS ---
  {
    id: 'drivers_license',
    kind: 'upload',
    docType: 'drivers_license',
    label: "Driver's License",
    description: 'Photo of your government-issued ID',
    icon: '🪪',
    required: 1,
  },
  {
    id: 'articles_incorporation',
    kind: 'upload',
    docType: 'articles_incorporation',
    label: 'Articles of Incorporation / Organization',
    description: 'Filed articles for your business entity',
    icon: '📄',
    required: 1,
  },
  {
    id: 'operating_agreement',
    kind: 'upload',
    docType: 'operating_agreement',
    label: 'Operating Agreement',
    description: 'Your LLC or partnership operating agreement',
    icon: '📄',
    required: 1,
  },
  // --- BANK STATEMENTS ---
  {
    id: 'business_bank_statement',
    kind: 'upload',
    docType: 'business_bank_statement',
    label: 'Business Bank Statements',
    description: '3 most recent business checking account statements',
    icon: '🏦',
    required: 3,
  },
  {
    id: 'personal_bank_statement',
    kind: 'upload',
    docType: 'personal_bank_statement',
    label: 'Personal Bank Statements',
    description: '3 most recent personal checking account statements',
    icon: '💳',
    required: 3,
  },
  // --- TAX & FINANCIALS ---
  {
    id: 'business_tax_return',
    kind: 'upload',
    docType: 'business_tax_return',
    label: 'Business Tax Returns',
    description: '2 most recent business tax returns (2024 & 2023)',
    icon: '📊',
    required: 2,
  },
  {
    id: 'personal_tax_return',
    kind: 'upload',
    docType: 'personal_tax_return',
    label: 'Personal Tax Returns',
    description: '2 most recent personal tax returns (2024 & 2023)',
    icon: '📝',
    required: 2,
  },
  {
    id: 'profit_loss',
    kind: 'upload',
    docType: 'profit_loss',
    label: 'Year-End 2025 Profit & Loss Statement',
    description: 'Annual P&L for your most recent fiscal year',
    icon: '📈',
    required: 1,
  },
  {
    id: 'balance_sheet',
    kind: 'upload',
    docType: 'balance_sheet',
    label: 'Year-End 2025 Balance Sheet',
    description: 'Annual balance sheet for your most recent fiscal year',
    icon: '📋',
    required: 1,
  },
];

/**
 * Fast Funding Package — core requirements (2 items).
 * Once both are complete the upsell cards appear.
 */
export const FAST_FUNDING_CHECKLIST = [
  {
    id: 'ff_core_funding_application',
    kind: 'form',
    label: 'Core Funding Application',
    description: 'Complete your business funding application',
    icon: '📝',
    appType: 'application',
  },
  {
    id: 'ff_business_bank_statement',
    kind: 'upload',
    docType: 'business_bank_statement',
    label: 'Business Bank Statements',
    description: '3 most recent business checking account statements',
    icon: '🏦',
    required: 3,
  },
];

/**
 * Fast Funding upsell cards — shown once the core checklist is 100 %.
 * Each card is a self-contained upload prompt.
 */
export const FAST_FUNDING_UPSELLS = [
  {
    id: 'upsell_tax_return',
    headline: 'Want more than $100,000?',
    description: 'Upload your most recent business tax return to unlock larger funding amounts.',
    icon: '💰',
    docType: 'business_tax_return',
    required: 1,
    cta: 'Upload Tax Return',
  },
  {
    id: 'upsell_equipment',
    headline: 'Want to Purchase Equipment?',
    description: 'Upload a quote or invoice for the equipment you want to buy.',
    icon: '⚙️',
    docType: 'equipment_quote',
    required: 1,
    cta: 'Upload Equipment Quote',
  },
  {
    id: 'upsell_payroll',
    headline: 'Want to Finance Payroll?',
    description: 'Upload your most recent payroll processing statement.',
    icon: '👥',
    docType: 'payroll_records',
    required: 1,
    cta: 'Upload Payroll Statement',
  },
  {
    id: 'upsell_invoices',
    headline: 'Want to Finance Invoices?',
    description: 'Upload a copy of an invoice for each customer you would like to finance.',
    icon: '📊',
    docType: 'sample_invoice',
    required: 1,
    cta: 'Upload Invoice',
  },
];
